import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '../../api';
import AccountSelector from '../../components/AccountSelector';

// ============================================================
// Sign Request form (Aug 13 revamp — Option B route-first scheduling)
//
// One step, no calendar. Every submission lands in the Sign Manager's
// Awaiting Scheduling queue; the Build Day optimizer plans efficient
// routes by real location, and the submitter gets a text the moment a
// work date is assigned. Emergency requests jump the queue and page the
// Sign Manager immediately.
// ============================================================

function fmtDuration(mins) {
  if (!mins) return '';
  if (mins < 60) return `${mins} minutes`;
  const h = mins / 60;
  return h === 1 ? '1 hour' : `${h} hours`;
}

const label = { fontWeight: 600, fontSize: 13, color: 'var(--text-primary)', display: 'block', marginBottom: 4 };
const req = <span style={{ color: '#ef4444' }}>*</span>;

export default function SignRequestFormPage() {
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState(null); // confirmation screen

  const [property, setProperty] = useState(null);
  const [useOtherAddress, setUseOtherAddress] = useState(false);
  const [workAddress, setWorkAddress] = useState('');
  const [region, setRegion] = useState('');
  const [workTypeIds, setWorkTypeIds] = useState([]);
  const [workDescription, setWorkDescription] = useState('');
  const [estimatedMinutes, setEstimatedMinutes] = useState('');
  const [specialSignDescription, setSpecialSignDescription] = useState('');
  const [accessInstructions, setAccessInstructions] = useState('');
  const [photos, setPhotos] = useState([]);
  const [isEmergency, setIsEmergency] = useState(false);

  useEffect(() => {
    api.getSignRequestConfig().then(setConfig).catch(e => setError(e.message));
  }, []);

  // Region auto-derives from the selected property; editable in case the
  // property has none (server validates either way).
  useEffect(() => {
    if (property?.region) setRegion(property.region);
  }, [property]);

  const selectedTypes = config?.work_types?.filter(wt => workTypeIds.includes(wt.id)) || [];
  const needsSpecialDesc = selectedTypes.some(wt => wt.requires_description);
  const needsPhotos = selectedTypes.filter(wt => wt.requires_photos);

  function toggleWorkType(id) {
    setWorkTypeIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function validate() {
    if (!property?.id) return 'Property is required';
    if (!region) return 'Region is required';
    if (workTypeIds.length === 0) return 'Select at least one type of work';
    if (!workDescription.trim()) return 'Work description is required';
    if (!estimatedMinutes) return 'Select how much time the work will take';
    if (needsSpecialDesc && !specialSignDescription.trim()) return 'Describe the custom/special sign';
    if (needsPhotos.length > 0 && photos.length === 0) {
      return `Photos are required for: ${needsPhotos.map(wt => wt.name).join(', ')}`;
    }
    if (useOtherAddress && !workAddress.trim()) return 'Enter the work address';
    return null;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const problem = validate();
    if (problem) return setError(problem);
    setError('');
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('property_id', property.id);
      fd.append('region', region);
      fd.append('work_type_ids', JSON.stringify(workTypeIds));
      fd.append('work_description', workDescription.trim());
      fd.append('estimated_minutes', String(estimatedMinutes));
      if (useOtherAddress && workAddress.trim()) fd.append('work_address', workAddress.trim());
      if (needsSpecialDesc) fd.append('special_sign_description', specialSignDescription.trim());
      if (accessInstructions.trim()) fd.append('access_instructions', accessInstructions.trim());
      if (isEmergency) fd.append('is_emergency', '1');
      for (const f of photos) fd.append('photos', f);

      const result = await api.submitSignRequest(fd);
      setCreated(result);
    } catch (e2) {
      setError(e2.message);
    } finally {
      setSubmitting(false);
    }
  }

  // ── confirmation screen ──
  if (created) {
    return (
      <div className="admin-page" style={{ maxWidth: 640 }}>
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 44, marginBottom: 8 }}>{created.is_emergency ? '🚨' : '✅'}</div>
          <h2 style={{ margin: '0 0 6px' }}>Sign Request #{created.id} submitted</h2>
          <p style={{ color: 'var(--text-muted)', marginTop: 0 }}>
            {created.is_emergency
              ? 'Emergency — the Sign Manager has been notified and will prioritize it.'
              : 'In the scheduling queue. You’ll get a text as soon as a work date is assigned.'}
          </p>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
            gap: 12, margin: '20px 0', textAlign: 'left',
          }}>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Property</div>
              <div style={{ fontWeight: 600 }}>{created.account_name || '—'}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Region</div>
              <div style={{ fontWeight: 600 }}>{created.sign_region}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Work</div>
              <div style={{ fontWeight: 600 }}>{created.sign_work_summary}</div>
            </div>
            <div className="card" style={{ padding: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 700 }}>Estimated</div>
              <div style={{ fontWeight: 600 }}>{fmtDuration(created.estimated_minutes)}</div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Link to={`/sales/requests/sign_request/${created.id}`} className="btn btn-primary">View Request</Link>
            <Link to="/sales/requests/sign_request" className="btn btn-muted">Back to Sign Requests</Link>
          </div>
        </div>
      </div>
    );
  }

  if (!config) {
    return <div className="admin-page"><p>{error || 'Loading…'}</p></div>;
  }

  return (
    <div className="admin-page" style={{ maxWidth: 720 }}>
      <div className="page-header">
        <h2>🪧 New Sign Request</h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
          Describe the work — the Sign Manager schedules the most efficient route and you&rsquo;ll be texted the work date.
        </p>
      </div>

      {error && <p className="alert alert-error">{error}</p>}

      <form onSubmit={handleSubmit} className="card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
        <AccountSelector
          value={property}
          onChange={setProperty}
          useOther={false}
          onToggleOther={() => {}}
          otherValue=""
          onOtherChange={() => {}}
          label="Property"
          required
        />

        {/* Work address — canonical property address by default */}
        {property && (
          <div>
            <label style={label}>Work Address</label>
            {!useOtherAddress ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 14, color: '#334155' }}>
                  {[property.address, property.city].filter(Boolean).join(', ') || 'No address on file'}
                </span>
                <button type="button" className="btn btn-muted" style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => setUseOtherAddress(true)}>
                  Use a different work address
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input className="form-input" style={{ flex: 1, padding: '10px 12px' }}
                  value={workAddress} onChange={e => setWorkAddress(e.target.value)}
                  placeholder="Street address, city" />
                <button type="button" className="btn btn-muted" style={{ padding: '4px 10px', fontSize: 12 }}
                  onClick={() => { setUseOtherAddress(false); setWorkAddress(''); }}>
                  Use property address
                </button>
              </div>
            )}
          </div>
        )}

        {/* Region — auto from property, editable */}
        <div>
          <label style={label}>Region {req}</label>
          <select className="form-input" style={{ width: 240, padding: '10px 12px' }}
            value={region} onChange={e => setRegion(e.target.value)}>
            <option value="">Select region…</option>
            {config.regions.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          {property?.region && region === property.region && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              Auto-filled from the property.
            </div>
          )}
        </div>

        {/* Work Requested — checkbox grid, multiple allowed */}
        <div>
          <label style={label}>Work Requested {req} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(check all that apply)</span></label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 8 }}>
            {config.work_types.map(wt => {
              const checked = workTypeIds.includes(wt.id);
              return (
                <label key={wt.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                  border: `2px solid ${checked ? '#0891b2' : 'var(--border-color, #e2e8f0)'}`,
                  borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: checked ? 600 : 400,
                  background: checked ? 'rgba(8, 145, 178, 0.06)' : 'transparent',
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggleWorkType(wt.id)} />
                  <span style={{ flex: 1 }}>{wt.name}</span>
                  {wt.requires_photos ? <span title="Photos required" style={{ fontSize: 12 }}>📷</span> : null}
                </label>
              );
            })}
          </div>
        </div>

        {/* Special sign description — only when Custom/Special checked */}
        {needsSpecialDesc && (
          <div>
            <label style={label}>Custom / Special Sign Description {req}</label>
            <textarea className="form-input" rows={3}
              value={specialSignDescription} onChange={e => setSpecialSignDescription(e.target.value)}
              placeholder="What does this sign need to say / look like?"
              style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
          </div>
        )}

        {/* Work description */}
        <div>
          <label style={label}>Work Description {req}</label>
          <textarea className="form-input" rows={4}
            value={workDescription} onChange={e => setWorkDescription(e.target.value)}
            placeholder="What needs to be done, where on the property, anything the tech should know"
            style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
        </div>

        {/* Duration */}
        <div>
          <label style={label}>How Much Time Will the Work Take? {req}</label>
          <select className="form-input" style={{ width: 240, padding: '10px 12px' }}
            value={estimatedMinutes} onChange={e => setEstimatedMinutes(Number(e.target.value) || '')}>
            <option value="">Select…</option>
            {config.duration_options.map(m => <option key={m} value={m}>{fmtDuration(m)}</option>)}
          </select>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
            Used to plan how many jobs fit on a route day.
          </div>
        </div>

        {/* Photos — optional unless a selected work type requires them */}
        <div>
          <label style={label}>
            Photos {needsPhotos.length > 0
              ? <span style={{ color: '#b91c1c' }}>* required for {needsPhotos.map(wt => wt.name).join(', ')}</span>
              : <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional, up to 10)</span>}
          </label>
          <input type="file" accept="image/*" multiple
            onChange={e => setPhotos(Array.from(e.target.files || []).slice(0, 10))} />
          {photos.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              {photos.length} photo{photos.length === 1 ? '' : 's'} attached
            </div>
          )}
        </div>

        {/* Access instructions — optional */}
        <div>
          <label style={label}>Access Instructions <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span></label>
          <textarea className="form-input" rows={2}
            value={accessInstructions} onChange={e => setAccessInstructions(e.target.value)}
            placeholder="Gate code, lockbox, who to contact on site…"
            style={{ width: '100%', padding: '10px 12px', resize: 'vertical' }} />
        </div>

        {/* Emergency option */}
        <label style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: 14,
          border: `2px solid ${isEmergency ? '#dc2626' : 'var(--border-color, #e2e8f0)'}`,
          borderRadius: 10, cursor: 'pointer',
          background: isEmergency ? 'rgba(220, 38, 38, 0.05)' : 'transparent',
        }}>
          <input type="checkbox" checked={isEmergency} style={{ marginTop: 3 }}
            onChange={e => setIsEmergency(e.target.checked)} />
          <span>
            <span style={{ fontWeight: 700, color: '#dc2626' }}>🚨 Emergency Request</span>
            <span style={{ display: 'block', fontSize: 13, color: 'var(--text-muted)', marginTop: 2 }}>
              Pages the Sign Manager immediately and jumps the scheduling queue.
            </span>
          </span>
        </label>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
          <button type="button" className="btn btn-muted" disabled={submitting}
            onClick={() => navigate('/sales/requests/sign_request')}>
            Cancel
          </button>
          <button type="submit" className="btn btn-gold" style={{ fontWeight: 700 }} disabled={submitting}>
            {submitting ? 'Submitting…' : isEmergency ? 'Submit Emergency Request' : 'Submit Sign Request'}
          </button>
        </div>
      </form>
    </div>
  );
}
